import CustomizeTagSelect from '@gp/components/CustomizeTagSelect';
import CustomizeTagSelectMultiple from '@gp/components/CustomizeTagSelectMultiple';
import GPSelect from '@gp/components/Select';
import GPSelectMultiple from '@gp/components/SelectMultiple';
import { SELECT_PANEL_HEIGHT_WITHOUT_SEARCH_BOX } from '@gp/constants/const';
import { getPlaceholder } from '@gp/utils/select';
import ComposedSelect from '@intelligence/components/ComposedSelect';
import classnames from 'classnames';
import { isEmpty } from 'lodash-es';
import React from 'react';
import { Input, TextArea } from 'tea-component';
import type {
  IDynamicComposedSelectFormControl,
  IDynamicCustomizeTagSelectFormControl,
  IDynamicDoubleSelectFormControl,
  IDynamicInputFormControl,
  IDynamicSelectFormControl,
  IDynamicSelectMultipleFormControl,
  IDynamicTextAreaFormControl,
} from '../types';

const BOX_CLASSNAME = 'globalpedia-custom-scrollbar globalpedia-modal-font14';

const renderInput = (props: IDynamicInputFormControl) => {
  const { label, placeholder: placeholderProps, onChange, isOnlyNum = false, ...restProps } = props;

  const placeholder = placeholderProps || getPlaceholder('input', label);

  const handleChange = (val: any) => {
    if (isOnlyNum) {
      // 使用正则表达式移除非数字字符
      const filteredValue = val?.replace(/[^\d]/g, '');
      onChange?.(filteredValue);
    } else {
      onChange?.(val);
    }
  };

  return <Input {...restProps} onChange={handleChange} placeholder={placeholder} />;
};

const renderTextArea = (props: IDynamicTextAreaFormControl) => {
  const { label, placeholder: placeholderProps, ...restProps } = props;

  const placeholder = placeholderProps || getPlaceholder('textarea', label);

  return <TextArea {...restProps} placeholder={placeholder} />;
};

const renderSelect = (props: IDynamicSelectFormControl) => {
  const { boxClassName = '', ...restProps } = props;

  return <GPSelect {...restProps} boxClassName={classnames('', BOX_CLASSNAME, boxClassName)} />;
};

const renderDoubleSelect = (props: IDynamicDoubleSelectFormControl) => {
  const { boxClassName = '', ...restProps } = props;

  const { style, value, studioOptionList } = restProps || {};

  const getPTeamValue = () => {
    const selectOption = studioOptionList?.find((item: any) => item.id === value) || {};
    if (!isEmpty(selectOption)) {
      const pTeamStr = selectOption?.pteam || '';
      return pTeamStr;
    }
    return '';
  };
  const getPTeamOptions = () => {
    const selectOption = studioOptionList?.find((item: any) => item.id === value) || {};
    if (!isEmpty(selectOption)) {
      const pTeamStr = selectOption?.pteam || '';
      return [
        {
          value: pTeamStr,
          text: pTeamStr,
        },
      ];
    }
    return [
      {
        value: '',
        text: '',
      },
    ];
  };

  return (
    <div className="dbg-team-game-information-edit-modal-content-form-Studio">
      <div className="dbg-team-game-information-edit-modal-content-form-Studio-left">
        <GPSelect
          {...restProps}
          label="Studio"
          searchable
          boxClassName={classnames('', BOX_CLASSNAME, boxClassName)}
        />
      </div>
      <div className="dbg-update-edit-divider">-</div>
      <div className="dbg-team-game-information-edit-modal-content-form-Studio-right">
        <GPSelect
          placeholder="Please select P-team"
          clearable={false}
          style={style}
          label="P-team"
          listHeight={SELECT_PANEL_HEIGHT_WITHOUT_SEARCH_BOX}
          options={getPTeamOptions()}
          value={getPTeamValue()}
          disabled={true}
        />
      </div>
    </div>
  );
};

const renderSelectMultiple = (props: IDynamicSelectMultipleFormControl) => {
  const { boxClassName, ...restProps } = props;

  return (
    <GPSelectMultiple {...restProps} boxClassName={classnames('', BOX_CLASSNAME, boxClassName)} />
  );
};

const renderCustomizeTagSelectMultiple = (props: any) => {
  const {
    boxClassName,
    generateCustomizeAddOptionDataHandle,
    generateCustomizeDeleteOptionDataHandle,
    ...restProps
  } = props;

  return (
    <CustomizeTagSelectMultiple
      {...restProps}
      ignoreCase={true}
      showPlaceholder={true}
      placeholderSelect={'Please select Engine'}
      placeholderInput={'Please input Engine name'}
      label={'Engine'}
      generateCustomizeAddOptionData={generateCustomizeAddOptionDataHandle}
      generateCustomizeDeleteOptionData={generateCustomizeDeleteOptionDataHandle}
    />
  );
};

export const renderComposedSelect = (props: IDynamicComposedSelectFormControl) => {
  const {
    leftLabel,
    leftPlaceholder: leftPlaceholderProps,
    rightLabel,
    rightPlaceholder: rightPlaceholderProps,
    leftProps,
    rightProps,
    value,
    ...restProps
  } = props;
  const leftPlaceholder = leftPlaceholderProps || getPlaceholder('select', leftLabel);
  const rightPlaceholder = rightPlaceholderProps || getPlaceholder('select', rightLabel);
  const { clearable: leftClearable } = leftProps || {};
  const { clearable: rightClearable } = rightProps || {};
  const { parent, child } = value || {};

  return (
    <ComposedSelect
      {...restProps}
      className="dbg-composed-select-wrapper"
      leftClassName="dbg-composed-select dbg-composed-select-left"
      rightClassName="dbg-composed-select dbg-composed-select-right"
      leftPlaceholder={leftPlaceholder}
      rightPlaceholder={rightPlaceholder}
      leftProps={{ clearable: leftClearable && !!parent }}
      rightProps={{ clearable: rightClearable && !!child }}
      value={value}
    />
  );
};

const renderCustomizeTagSelect = (props: IDynamicCustomizeTagSelectFormControl) => {
  return <CustomizeTagSelect {...props} />;
};

export const CONTROL_RENDERERS: {
  input: (props: IDynamicInputFormControl) => React.ReactElement;
  textarea: (props: IDynamicTextAreaFormControl) => React.ReactElement;
  select: (props: IDynamicSelectFormControl) => React.ReactElement;
  selectMultiple: (props: IDynamicSelectMultipleFormControl) => React.ReactElement;
  composedSelect: (props: IDynamicComposedSelectFormControl) => React.ReactElement;
  customizeTagSelect: (props: IDynamicCustomizeTagSelectFormControl) => React.ReactElement;
  doubleSelect: (props: IDynamicDoubleSelectFormControl) => React.ReactElement;
  customizeTagSelectMultiple: (props: IDynamicCustomizeTagSelectFormControl) => React.ReactElement;
} = {
  input: renderInput,
  textarea: renderTextArea,
  select: renderSelect,
  selectMultiple: renderSelectMultiple,
  composedSelect: renderComposedSelect,
  customizeTagSelect: renderCustomizeTagSelect,
  doubleSelect: renderDoubleSelect,
  customizeTagSelectMultiple: renderCustomizeTagSelectMultiple,
};
